import { FormEvent, useEffect, useState } from 'react';
import { CheckCircleIcon, NoSymbolIcon, KeyIcon } from '@heroicons/react/24/outline';
import { AdminRole, AdminUser, CrmUser } from './types';
import { createUser, getErrorMessage, listUsers, updateUser } from './adminApi';
import { VISITE_NIVEAUX, formatDate, roleLabel } from './crmShared';

// Toggle chips for assigning niveaux. No selection = all niveaux.
function NiveauPicker({
  value,
  onToggle,
  disabled,
}: {
  value: string[];
  onToggle: (niveau: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {VISITE_NIVEAUX.map((n) => {
        const on = value.includes(n);
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(n)}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
              on ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

const toggle = (list: string[], n: string): string[] =>
  list.includes(n) ? list.filter((x) => x !== n) : [...list, n];

export default function UsersPage({ currentUser }: { currentUser: AdminUser | null }) {
  const [users, setUsers] = useState<CrmUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AdminRole>('TELECONSEILLER');
  const [niveaux, setNiveaux] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    listUsers()
      .then(setUsers)
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreating(true);
    setError('');
    try {
      const created = await createUser({
        email,
        name: name.trim() || undefined,
        password,
        role,
        niveaux: role === 'TELECONSEILLER' ? niveaux : [],
      });
      setUsers((prev) => [...prev, created]);
      setEmail('');
      setName('');
      setPassword('');
      setRole('TELECONSEILLER');
      setNiveaux([]);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const patch = async (id: string, payload: Parameters<typeof updateUser>[1]) => {
    setBusyId(id);
    setError('');
    try {
      const updated = await updateUser(id, payload);
      setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusyId('');
    }
  };

  const resetPassword = (user: CrmUser) => {
    const next = window.prompt(`Nouveau mot de passe pour ${user.email} (8 caractères min) :`);
    if (next === null) return;
    if (next.length < 8) { setError('Le mot de passe doit faire au moins 8 caractères.'); return; }
    patch(user.id, { password: next });
  };

  return (
    <div className="px-4 py-6 lg:px-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-950">Utilisateurs</h2>
        <p className="mt-1 text-sm text-gray-500">Comptes administrateurs et téléconseillers</p>
      </div>

      {error && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <form onSubmit={handleCreate} className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Créer un compte</h3>
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_160px_auto]">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="off"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom (affiché dans les stats)"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe (8+ car.)"
            autoComplete="new-password"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as AdminRole)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="TELECONSEILLER">Téléconseiller</option>
            <option value="ADMIN">Administrateur</option>
          </select>
          <button
            type="submit"
            disabled={creating}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
          >
            {creating ? 'Création…' : 'Créer'}
          </button>
        </div>
        {role === 'TELECONSEILLER' && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-gray-500">
              Niveaux attribués <span className="text-gray-400">(aucun sélectionné = tous les niveaux)</span>
            </p>
            <NiveauPicker value={niveaux} onToggle={(n) => setNiveaux((prev) => toggle(prev, n))} />
          </div>
        )}
      </form>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Utilisateur</th>
                <th className="px-4 py-3">Rôle</th>
                <th className="px-4 py-3">Niveaux</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Dernière connexion</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">Chargement…</td>
                </tr>
              ) : (
                users.map((user) => {
                  const isSelf = user.id === currentUser?.id;
                  const busy = busyId === user.id;
                  return (
                    <tr key={user.id} className={user.isActive ? '' : 'bg-gray-50/60'}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-950">
                          {user.name ?? '—'}
                          {isSelf && <span className="ml-2 text-xs font-normal text-gray-400">(vous)</span>}
                        </div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={user.role}
                          disabled={busy || isSelf}
                          onChange={(e) => patch(user.id, { role: e.target.value as AdminRole })}
                          className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20"
                          aria-label="Rôle"
                          title={isSelf ? 'Vous ne pouvez pas changer votre propre rôle' : undefined}
                        >
                          <option value="TELECONSEILLER">{roleLabel('TELECONSEILLER')}</option>
                          <option value="ADMIN">{roleLabel('ADMIN')}</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {user.role === 'ADMIN' ? (
                          <span className="text-xs text-gray-400">Tous (admin)</span>
                        ) : (
                          <div>
                            <NiveauPicker
                              value={user.niveaux}
                              disabled={busy}
                              onToggle={(n) => patch(user.id, { niveaux: toggle(user.niveaux, n) })}
                            />
                            {user.niveaux.length === 0 && (
                              <p className="mt-1 text-xs text-gray-400">Tous les niveaux</p>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.isActive ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                            <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" /> Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600">
                            <NoSymbolIcon className="h-3.5 w-3.5" aria-hidden="true" /> Désactivé
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                        {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Jamais'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => resetPassword(user)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                          >
                            <KeyIcon className="h-4 w-4" aria-hidden="true" />
                            MDP
                          </button>
                          <button
                            type="button"
                            onClick={() => patch(user.id, { isActive: !user.isActive })}
                            disabled={busy || isSelf}
                            className={`rounded-md border px-2.5 py-1.5 text-xs font-medium disabled:opacity-50 ${
                              user.isActive
                                ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                                : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            }`}
                            title={isSelf ? 'Vous ne pouvez pas désactiver votre propre compte' : undefined}
                          >
                            {user.isActive ? 'Désactiver' : 'Activer'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
